import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import type { TierStats } from '../../types';
import { TIER_COLORS, TIER_LABELS } from './tierChartConfig';
import {
  CHART_LABEL_LINE_STYLE,
  CHART_LEGEND_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from './chartTheme';
import { ChartEmptyState, ChartPanel, ChartSliceGradient } from './ChartPrimitives';
import { chartGradientFill, useChartGradientId } from './chartGradient';

interface TierBreakdownChartProps {
  stats: TierStats[];
  title?: string;
}

export function TierBreakdownChart({
  stats,
  title = 'Generation Tier Breakdown',
}: TierBreakdownChartProps) {
  const gradientBaseId = useChartGradientId('tier-slice');
  const data = stats
    .filter((item) => item.count > 0)
    .map((item) => ({
      ...item,
      name: TIER_LABELS[item.tier],
      value: item.count,
      color: TIER_COLORS[item.tier],
    }));
  const total = stats.reduce((sum, item) => sum + item.count, 0);

  return (
    <ChartPanel
      title={title}
      iconClassName="bg-purple-50 text-purple-600"
      icon={
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
        </svg>
      }
      summary={
        <div className="text-right">
          <p className="text-lg font-semibold leading-none text-slate-700">{total}</p>
          <p className="mt-1 text-[10px] uppercase text-slate-500">Scans</p>
        </div>
      }
    >

      {data.length > 0 ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <defs>
                {data.map((item) => (
                  <ChartSliceGradient
                    key={item.tier}
                    id={`${gradientBaseId}-${item.tier}`}
                    color={item.color}
                  />
                ))}
              </defs>
              <Pie
                data={data}
                cx="50%"
                cy="48%"
                innerRadius="38%"
                outerRadius="62%"
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) => `${name}: ${percent.toFixed(1)}%`}
                labelLine={CHART_LABEL_LINE_STYLE}
                isAnimationActive={false}
              >
                {data.map((item) => (
                  <Cell
                    key={item.tier}
                    fill={chartGradientFill(`${gradientBaseId}-${item.tier}`)}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                formatter={(value: number, name: string) => [`${value} scans`, name]}
              />
              <Legend
                verticalAlign="top"
                height={24}
                wrapperStyle={CHART_LEGEND_STYLE}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <ChartEmptyState>No tier data available</ChartEmptyState>
      )}
    </ChartPanel>
  );
}
